import { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { connect, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Form, Input } from "reactstrap";
import Popup from "reactjs-popup";
import swal from "sweetalert";
import BreadCrumbs from "../../components/breadCrumbs";
import SideNav from "../../components/sideNav";
import TopNav from "../../components/topNav";
import envConfig from "../../config/constants";
import {
  checkEditAccessRight,
  verifyReducingBalance,
} from "../../config/utility";
import {
  archiveWipProject,
  completeWipProject,
  createWipInvoice,
  deleteWipInvoice,
  getSubclasses,
  getWipProject,
  updateWipInvoice,
  updateWipProject,
} from "../../redux/api";
import { setMessageUser } from "../../redux/features/user";
import { getAuthKey, getUserData } from "../../redux/selector";
import { cross, deleteAsset, edit } from "../../config/assets";
import { openingDateToYear, openingYearToDate } from "../Wip/helpers";
import "../Wip/wip.scss";

const money = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const blankInvoice = {
  id: null,
  invoice_number: "",
  invoice_date: "",
  supplier: "",
  description: "",
  amount: "",
};
const internalActivityFields = new Set([
  "id",
  "invoice_id",
  "project_id",
  "user_id",
  "created_by",
  "updated_by",
]);
const isOpaqueIdentifier = (value) => /^[a-f0-9]{32,}$/i.test(String(value));
const activityDetails = (details) =>
  Object.entries(details || {}).filter(
    ([key, value]) =>
      !internalActivityFields.has(key) &&
      value !== null &&
      value !== undefined &&
      value !== "",
  );

const WipDetail = (props) => {
  const { project_id: projectId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isReadOnly = checkEditAccessRight(props.userData.access_right);
  const isReducingBalance = verifyReducingBalance(
    props.userData.organization.depreciation_type,
  );
  const classes = useMemo(
    () =>
      props.userData.classes.filter(
        (item) => Number(item.default_class_id) !== envConfig.defaultClass.land,
      ),
    [props.userData.classes],
  );
  const [data, setData] = useState(null);
  const [editingProject, setEditingProject] = useState("");
  const [invoiceBox, setInvoiceBox] = useState(false);
  const [completionBox, setCompletionBox] = useState(false);
  const [historyBox, setHistoryBox] = useState(false);
  const [projectForm, setProjectForm] = useState({
    name: "",
    opening_balance: "0",
    opening_year: "",
  });
  const [invoice, setInvoice] = useState(blankInvoice);
  const [subclasses, setSubclasses] = useState([]);
  const [completion, setCompletion] = useState({
    completion_date: "",
    class_id: "",
    subclass_id: "",
    description: "",
    useful_life: "",
    depreciation_percentage: "",
    residual_value: "0",
  });
  const financialYearStart = props.userData.organization.fy_start;
  const maximumOpeningYear = openingDateToYear(financialYearStart);
  const customStyles = {
    headCells: {
      style: {
        fontWeight: "600",
        fontSize: "15px",
        textTransform: "capitalize",
      },
    },
    rows: { style: { minHeight: "3rem" } },
  };

  const load = useCallback(async () => {
    const response = await dispatch(
      getWipProject({ authKey: props.authKey, projectId }),
    );
    if (response.payload?.status !== 200) {
      navigate("/app/wip");
      return;
    }
    setData(response.payload.data);
    const project = response.payload.data.project;
    setProjectForm({
      name: project.name,
      opening_balance: String(project.opening_balance),
      opening_year: openingDateToYear(project.opening_balance_date),
    });
    setCompletion((current) => ({
      ...current,
      description: current.description || project.name,
    }));
  }, [dispatch, navigate, projectId, props.authKey]);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (response) =>
    props.setMessageUser({
      message: response.payload?.message || "Request failed",
      type:
        response.payload?.status === 200
          ? envConfig.notificationTypes.success
          : envConfig.notificationTypes.danger,
    });
  const submitAndReload = async (request) => {
    const response = await request;
    notify(response);
    if (response.payload?.status === 200) await load();
    return response;
  };

  const selectClass = async (classId) => {
    setCompletion({ ...completion, class_id: classId, subclass_id: "" });
    if (!classId) {
      setSubclasses([]);
      return;
    }
    const response = await dispatch(
      getSubclasses({ authKey: props.authKey, classId }),
    );
    setSubclasses(
      response.payload?.status === 200
        ? response.payload.data.filter((item) => Number(item.status) === 1)
        : [],
    );
  };

  const saveProject = async () => {
    const response = await submitAndReload(
      dispatch(
        updateWipProject({
          authKey: props.authKey,
          projectId,
          body: JSON.stringify({
            name: projectForm.name,
            opening_balance: Number(projectForm.opening_balance),
            opening_balance_date: openingYearToDate(
              projectForm.opening_year,
              financialYearStart,
            ),
          }),
        }),
      ),
    );
    if (response.payload?.status === 200) setEditingProject("");
  };

  const saveInvoice = async () => {
    const body = JSON.stringify({
      ...invoice,
      amount: Number(invoice.amount),
    });
    const request = invoice.id
      ? dispatch(
          updateWipInvoice({
            authKey: props.authKey,
            projectId,
            invoiceId: invoice.id,
            body,
          }),
        )
      : dispatch(
          createWipInvoice({
            authKey: props.authKey,
            projectId,
            body,
          }),
        );
    const response = await submitAndReload(request);
    if (response.payload?.status === 200) {
      setInvoice(blankInvoice);
      setInvoiceBox(false);
    }
  };

  const completeProject = async () => {
    const confirmed = await swal({
      title: "Transfer project to PPE?",
      text: "This permanently transfers the full WIP balance and locks the project and its invoices.",
      icon: "warning",
      buttons: ["Cancel", "Transfer to PPE"],
      dangerMode: true,
    });
    if (!confirmed) return;
    const response = await dispatch(
      completeWipProject({
        authKey: props.authKey,
        projectId,
        body: JSON.stringify({
          ...completion,
          class_id: Number(completion.class_id),
          subclass_id: completion.subclass_id
            ? Number(completion.subclass_id)
            : null,
          useful_life: Number(completion.useful_life),
          depreciation_percentage: isReducingBalance
            ? Number(completion.depreciation_percentage)
            : undefined,
          residual_value: isReducingBalance
            ? Number(completion.residual_value)
            : 0,
        }),
      }),
    );
    notify(response);
    if (response.payload?.status === 200) {
      const assetClass = classes.find(
        (item) => Number(item.id) === Number(completion.class_id),
      );
      if (!assetClass) {
        props.setMessageUser({
          message:
            "The asset was created, but its destination class is no longer available. Refresh the page to view the updated data.",
          type: envConfig.notificationTypes.danger,
        });
        return;
      }
      navigate(
        `/app/asset-detail/${assetClass.name}/${assetClass.id}/${response.payload.data.id}`,
      );
    }
  };

  if (!data)
    return (
      <>
        <TopNav />
        <SideNav />
        <div className="listings">
          <div className="listing">Loading WIP project…</div>
        </div>
      </>
    );
  const { project, invoices, history } = data;
  const editable = !isReadOnly && project.status === "ACTIVE";
  const canArchive =
    editable && Number(project.total_wip) === 0 && invoices.length === 0;
  const editInvoice = (item) => {
    setInvoice({
      ...item,
      invoice_date: String(item.invoice_date).slice(0, 10),
      amount: String(item.amount),
    });
    setInvoiceBox(true);
  };
  const editableInvoiceCell = (value, row, field) => (
    <span className="wip-table-value">
      {value}
      {editable && (
        <button
          className="wip-edit-icon"
          type="button"
          aria-label={`Edit ${field} for invoice ${row.invoice_number}`}
          onClick={() => editInvoice(row)}
        >
          <img src={edit} alt="" />
        </button>
      )}
    </span>
  );
  const invoiceColumns = [
    {
      name: "Invoice",
      minWidth: "150px",
      cell: (row) => editableInvoiceCell(row.invoice_number, row, "number"),
    },
    {
      name: "Date",
      cell: (row) =>
        editableInvoiceCell(String(row.invoice_date).slice(0, 10), row, "date"),
      minWidth: "135px",
    },
    {
      name: "Supplier",
      cell: (row) => editableInvoiceCell(row.supplier, row, "supplier"),
      minWidth: "160px",
    },
    {
      name: "Description",
      cell: (row) =>
        editableInvoiceCell(row.description || "-", row, "description"),
      minWidth: "190px",
    },
    {
      name: "Amount",
      cell: (row) => editableInvoiceCell(money(row.amount), row, "amount"),
      right: true,
      minWidth: "130px",
    },
    ...(editable
      ? [
          {
            name: "Actions",
            width: "90px",
            center: true,
            cell: (row) => (
              <button
                className="wip-delete-icon"
                type="button"
                aria-label={`Delete invoice ${row.invoice_number}`}
                onClick={async () => {
                  const confirmed = await swal({
                    title: "Remove invoice?",
                    text: `Remove invoice ${row.invoice_number}? This cannot be undone.`,
                    icon: "warning",
                    buttons: ["Cancel", "Remove"],
                    dangerMode: true,
                  });
                  if (!confirmed) return;
                  await submitAndReload(
                    dispatch(
                      deleteWipInvoice({
                        authKey: props.authKey,
                        projectId,
                        invoiceId: row.id,
                      }),
                    ),
                  );
                }}
              >
                <img src={deleteAsset} alt="" />
              </button>
            ),
          },
        ]
      : []),
  ];

  const iconEdit = (label, action) =>
    editable && (
      <button
        className="wip-edit-icon"
        type="button"
        aria-label={label}
        onClick={action}
      >
        <img src={edit} alt="" />
      </button>
    );

  return (
    <>
      <TopNav />
      <SideNav />
      <div className="listings">
        <BreadCrumbs crumbs={["Work in Progress", project.name]} />
        <div className="wip-page">
          <section className="wip-card wip-project-detail">
            <div className="wip-detail-header">
              <button
                className="wip-last-updated"
                type="button"
                onClick={() => setHistoryBox(true)}
              >
                Last Updated on {new Date(project.updated_on).toLocaleString()}
              </button>
              <div className="wip-detail-actions">
                {editable && Number(project.total_wip) > 0 && (
                  <button type="button" onClick={() => setCompletionBox(true)}>
                    Transfer to PPE
                  </button>
                )}
                <button type="button" onClick={() => setHistoryBox(true)}>
                  History
                </button>
                {canArchive && (
                  <Popup
                    trigger={
                      <button
                        className="wip-overflow"
                        type="button"
                        aria-label="Project actions"
                      >
                        &#8942;
                      </button>
                    }
                    position="bottom right"
                    closeOnDocumentClick
                  >
                    <button
                      className="wip-overflow__item"
                      type="button"
                      onClick={async () => {
                        const confirmed = await swal({
                          title: "Archive project?",
                          text: "Archive this empty WIP project?",
                          icon: "warning",
                          buttons: ["Cancel", "Archive"],
                          dangerMode: true,
                        });
                        if (!confirmed) return;
                        const response = await dispatch(
                          archiveWipProject({
                            authKey: props.authKey,
                            projectId,
                          }),
                        );
                        notify(response);
                        if (response.payload?.status === 200)
                          navigate("/app/wip");
                      }}
                    >
                      Archive project
                    </button>
                  </Popup>
                )}
              </div>
            </div>
            <div className="wip-detail-grid">
              <div>
                <strong>Project name</strong>
                <span>
                  {project.name}
                  {iconEdit("Edit project name", () =>
                    setEditingProject("name"),
                  )}
                </span>
              </div>
              <div>
                <strong>Status</strong>
                <span>{project.status.replaceAll("_", " ")}</span>
              </div>
              <div>
                <strong>Opening balance</strong>
                <span>
                  {money(project.opening_balance)}
                  {iconEdit("Edit opening balance", () =>
                    setEditingProject("opening"),
                  )}
                </span>
              </div>
              <div>
                <strong>Opening financial year</strong>
                <span>
                  {openingDateToYear(project.opening_balance_date) || "-"}
                  {iconEdit("Edit opening financial year", () =>
                    setEditingProject("opening"),
                  )}
                </span>
              </div>
              <div>
                <strong>Invoice total</strong>
                <span>{money(project.invoice_total)}</span>
              </div>
              <div>
                <strong>Total WIP</strong>
                <span>{money(project.total_wip)}</span>
              </div>
              {project.completion_date && (
                <>
                  <div>
                    <strong>Completion date</strong>
                    <span>{String(project.completion_date).slice(0, 10)}</span>
                  </div>
                  <div>
                    <strong>Transferred amount</strong>
                    <span>{money(project.transferred_amount)}</span>
                  </div>
                </>
              )}
              {project.completed_asset_id && project.completed_class_id && (
                <div>
                  <strong>Linked PPE asset</strong>
                  <Link
                    to={`/app/asset-detail/${project.completed_class_name}/${project.completed_class_id}/${project.completed_asset_id}`}
                  >
                    Open asset
                  </Link>
                </div>
              )}
            </div>
          </section>

          <section className="wip-card wip-invoices">
            <div className="wip-card__header">
              <h4>Invoices</h4>
              {editable && (
                <Button
                  className="btn btn__blue btn__brd"
                  onClick={() => {
                    setInvoice(blankInvoice);
                    setInvoiceBox(true);
                  }}
                >
                  + Add Invoice
                </Button>
              )}
            </div>
            <DataTable
              columns={invoiceColumns}
              data={invoices}
              highlightOnHover
              pagination
              persistTableHead
              customStyles={customStyles}
              noDataComponent="No invoices have been added."
            />
          </section>
        </div>
      </div>

      <Popup
        className="wip-popup"
        open={Boolean(editingProject)}
        closeOnDocumentClick={false}
      >
        <div className="modal popup__modal wip-modal">
          <div className="header popup__header mb-sm">
            <h2 className="heading-2 popup__blue">
              {editingProject === "name"
                ? "Edit Project Name"
                : "Edit Opening Balance"}
            </h2>
            <button
              className="popup__close close"
              type="button"
              onClick={() => setEditingProject("")}
            >
              <img src={cross} alt="Close" />
            </button>
          </div>
          <Form
            className="wip-popup-form"
            onSubmit={(event) => {
              event.preventDefault();
              saveProject();
            }}
          >
            {editingProject === "name" ? (
              <>
                <label className="form__label">
                  Project name
                  <Input
                    className="form__input"
                    required
                    maxLength={100}
                    value={projectForm.name}
                    onChange={(event) =>
                      setProjectForm({
                        ...projectForm,
                        name: event.target.value,
                      })
                    }
                  />
                </label>
                {!projectForm.opening_year && (
                  <label className="form__label">
                    Opening financial year
                    <Input
                      className="form__input"
                      required
                      min="1900"
                      max={maximumOpeningYear}
                      type="number"
                      placeholder="YYYY"
                      value={projectForm.opening_year}
                      onChange={(event) =>
                        setProjectForm({
                          ...projectForm,
                          opening_year: event.target.value,
                        })
                      }
                    />
                  </label>
                )}
              </>
            ) : (
              <>
                <label className="form__label">
                  Opening balance
                  <Input
                    className="form__input"
                    required
                    min="0"
                    step="0.01"
                    type="number"
                    value={projectForm.opening_balance}
                    onChange={(event) =>
                      setProjectForm({
                        ...projectForm,
                        opening_balance: event.target.value,
                      })
                    }
                  />
                </label>
                <label className="form__label">
                  Opening financial year
                  <Input
                    className="form__input"
                    required
                    min="1900"
                    max={maximumOpeningYear}
                    type="number"
                    placeholder="YYYY"
                    value={projectForm.opening_year}
                    onChange={(event) =>
                      setProjectForm({
                        ...projectForm,
                        opening_year: event.target.value,
                      })
                    }
                  />
                </label>
              </>
            )}
            <div className="popup__btns">
              <Button
                className="btn btn__border popup__btn"
                type="button"
                onClick={() => setEditingProject("")}
              >
                Cancel
              </Button>
              <Button className="btn btn__blue popup__btn" type="submit">
                Save
              </Button>
            </div>
          </Form>
        </div>
      </Popup>

      <Popup
        className="wip-popup"
        open={invoiceBox}
        closeOnDocumentClick={false}
      >
        <div className="modal popup__modal wip-modal">
          <div className="header popup__header mb-sm">
            <h2 className="heading-2 popup__blue">
              {invoice.id ? "Edit Invoice" : "Add Invoice"}
            </h2>
            <button
              className="popup__close close"
              type="button"
              onClick={() => {
                setInvoice(blankInvoice);
                setInvoiceBox(false);
              }}
            >
              <img src={cross} alt="Close" />
            </button>
          </div>
          <Form
            className="wip-popup-form wip-popup-form--two"
            onSubmit={(event) => {
              event.preventDefault();
              saveInvoice();
            }}
          >
            <label className="form__label">
              Invoice number
              <Input
                className="form__input"
                required
                maxLength={100}
                value={invoice.invoice_number}
                onChange={(event) =>
                  setInvoice({ ...invoice, invoice_number: event.target.value })
                }
              />
            </label>
            <label className="form__label">
              Invoice date
              <Input
                className="form__input"
                required
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={invoice.invoice_date}
                onChange={(event) =>
                  setInvoice({ ...invoice, invoice_date: event.target.value })
                }
              />
            </label>
            <label className="form__label">
              Supplier
              <Input
                className="form__input"
                required
                maxLength={200}
                value={invoice.supplier}
                onChange={(event) =>
                  setInvoice({ ...invoice, supplier: event.target.value })
                }
              />
            </label>
            <label className="form__label">
              Description
              <Input
                className="form__input"
                maxLength={512}
                value={invoice.description}
                onChange={(event) =>
                  setInvoice({ ...invoice, description: event.target.value })
                }
              />
            </label>
            <label className="form__label">
              Amount
              <Input
                className="form__input"
                required
                min="0.01"
                step="0.01"
                type="number"
                value={invoice.amount}
                onChange={(event) =>
                  setInvoice({ ...invoice, amount: event.target.value })
                }
              />
            </label>
            <div className="popup__btns">
              <Button
                className="btn btn__border popup__btn"
                type="button"
                onClick={() => {
                  setInvoice(blankInvoice);
                  setInvoiceBox(false);
                }}
              >
                Cancel
              </Button>
              <Button className="btn btn__blue popup__btn" type="submit">
                {invoice.id ? "Save Invoice" : "Add Invoice"}
              </Button>
            </div>
          </Form>
        </div>
      </Popup>

      <Popup
        className="wip-popup"
        open={completionBox}
        closeOnDocumentClick={false}
      >
        <div className="modal popup__modal wip-modal wip-completion-modal">
          <div className="header popup__header mb-sm">
            <div>
              <h2 className="heading-2 popup__blue">Transfer to PPE</h2>
              <p>
                This creates one Construction asset for{" "}
                {money(project.total_wip)}. The project and invoices become
                immutable.
              </p>
            </div>
            <button
              className="popup__close close"
              type="button"
              onClick={() => setCompletionBox(false)}
            >
              <img src={cross} alt="Close" />
            </button>
          </div>
          <Form
            className="wip-popup-form wip-popup-form--two"
            onSubmit={(event) => {
              event.preventDefault();
              completeProject();
            }}
          >
            <label className="form__label">
              Completion date
              <Input
                className="form__input"
                required
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={completion.completion_date}
                onChange={(event) =>
                  setCompletion({
                    ...completion,
                    completion_date: event.target.value,
                  })
                }
              />
            </label>
            <label className="form__label">
              Destination class
              <Input
                className="form__input"
                required
                type="select"
                value={completion.class_id}
                onChange={(event) => selectClass(event.target.value)}
              >
                <option value="">Select class</option>
                {classes.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                  </option>
                ))}
              </Input>
            </label>
            <label className="form__label">
              Subclass
              <Input
                className="form__input"
                type="select"
                value={completion.subclass_id}
                onChange={(event) =>
                  setCompletion({
                    ...completion,
                    subclass_id: event.target.value,
                  })
                }
              >
                <option value="">Unassigned</option>
                {subclasses.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                  </option>
                ))}
              </Input>
            </label>
            <label className="form__label">
              Description
              <Input
                className="form__input"
                required
                maxLength={256}
                value={completion.description}
                onChange={(event) =>
                  setCompletion({
                    ...completion,
                    description: event.target.value,
                  })
                }
              />
            </label>
            <label className="form__label">
              Useful life
              <Input
                className="form__input"
                required
                min="0.01"
                step="0.01"
                type="number"
                value={completion.useful_life}
                onChange={(event) =>
                  setCompletion({
                    ...completion,
                    useful_life: event.target.value,
                  })
                }
              />
            </label>
            {isReducingBalance && (
              <>
                <label className="form__label">
                  Depreciation percentage
                  <Input
                    className="form__input"
                    required
                    min="0.01"
                    max="100"
                    step="0.01"
                    type="number"
                    value={completion.depreciation_percentage}
                    onChange={(event) =>
                      setCompletion({
                        ...completion,
                        depreciation_percentage: event.target.value,
                      })
                    }
                  />
                </label>
                <label className="form__label">
                  Residual value
                  <Input
                    className="form__input"
                    required
                    min="0"
                    max={project.total_wip / 2}
                    step="0.01"
                    type="number"
                    value={completion.residual_value}
                    onChange={(event) =>
                      setCompletion({
                        ...completion,
                        residual_value: event.target.value,
                      })
                    }
                  />
                </label>
              </>
            )}
            <div className="popup__btns">
              <Button
                className="btn btn__border popup__btn"
                type="button"
                onClick={() => setCompletionBox(false)}
              >
                Cancel
              </Button>
              <Button className="btn btn__blue popup__btn" type="submit">
                Transfer to PPE
              </Button>
            </div>
          </Form>
        </div>
      </Popup>

      {historyBox && (
        <div className="history__popup wip-history-popup">
          <aside className="history__box wip-history-panel">
            <div className="header popup__header mb-sm">
              <h2 className="heading-2 popup__blue">Project History</h2>
              <button
                className="wip-history-close"
                type="button"
                onClick={() => setHistoryBox(false)}
              >
                Close
              </button>
            </div>
            <div className="wip-history-list">
              {history.map((item) => (
                <article className="wip-history-item" key={item.id}>
                  <span className="wip-history-item__dot" />
                  <div>
                    <strong>{item.action.replaceAll("_", " ")}</strong>
                    <small>{new Date(item.created_on).toLocaleString()}</small>
                    {item.user_name && !isOpaqueIdentifier(item.user_name) && (
                      <span>By {item.user_name}</span>
                    )}
                    {activityDetails(item.details).length > 0 && (
                      <dl>
                        {activityDetails(item.details).map(([key, value]) => (
                          <div key={key}>
                            <dt>{key.replaceAll("_", " ")}</dt>
                            <dd>
                              {typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                </article>
              ))}
              {!history.length && <p>No project activity found.</p>}
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default connect(
  (state) => ({ authKey: getAuthKey(state), userData: getUserData(state) }),
  { setMessageUser },
)(WipDetail);
